import { useSyncExternalStore } from "react";

/** Whether the browser has a connection (updates as it comes and goes). */
export function useOnline(): boolean {
  return useSyncExternalStore(
    (on) => {
      window.addEventListener("online", on);
      window.addEventListener("offline", on);
      return () => {
        window.removeEventListener("online", on);
        window.removeEventListener("offline", on);
      };
    },
    () => navigator.onLine,
  );
}

// The browser's offer to install the app (Chrome, Edge, Android), kept for the menu
// instead of its own banner. Safari has none: there it's "Add to Home Screen".
interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
let offer: InstallPrompt | null = null;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  offer = e as InstallPrompt;
  changed();
});
window.addEventListener("appinstalled", () => {
  offer = null;
  changed();
});

const standalone = () => window.matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
const iOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

/**
 * How this device can install the app: "prompt" (the browser asks), "ios" (by hand,
 * from Safari's share menu), or null (installed already, or not possible here).
 */
export function useInstall(): { how: "prompt" | "ios" | null; install: () => Promise<void> } {
  const available = useSyncExternalStore(
    (on) => {
      listeners.add(on);
      return () => listeners.delete(on);
    },
    () => offer != null,
  );
  const how = standalone() ? null : available ? "prompt" : iOS() ? "ios" : null;
  return {
    how,
    install: async () => {
      if (!offer) return;
      await offer.prompt();
      if ((await offer.userChoice).outcome === "accepted") offer = null;
      changed();
    },
  };
}
